import {Component, Input, OnInit} from '@angular/core';
import {FormArray, FormBuilder, FormControl, FormGroup} from "@angular/forms";
import {TranslocoService} from "@ngneat/transloco";
import {AbstractComponent} from "@components";
import {Subscription} from 'rxjs';
import {
  EventItem,
  EventPaymentSettings, Language, Lookups,
  OrganiserPaymentData,
  Race, RACE_COMMERCIAL_ITEM_DEFAULT,
  RaceCommercialItem,
  RaceCommercialItemPeriod,
  TOAST_STATE
} from "@model";
import { ToastService} from "@services";
import {required} from "@utility";
import {NgbActiveModal} from "@ng-bootstrap/ng-bootstrap";

@Component({
  selector: 'app-race-commercial-item-form',
  templateUrl: './race-commercial-item-form.component.html',
  styleUrls: ['./race-commercial-item-form.component.scss']
})

export class RaceCommercialItemFormComponent extends AbstractComponent implements OnInit {
  @Input() event: EventItem | undefined;
  @Input() race: Race | undefined;
  @Input() disableEditing: boolean = false;
  @Input() showDropdown: boolean = false;
  @Input() eventPaymentSettings: EventPaymentSettings | null = null;
  @Input() organiserPaymentData: OrganiserPaymentData | null = null;
  @Input() currentRaceCommercialItem: RaceCommercialItem = new RaceCommercialItem();
  @Input() raceCommercialItems: RaceCommercialItem[] = [];
  @Input() registrationFeeTypes: Lookups[] = [];
  @Input() currencies: Lookups[] = [];
  @Input() title: string | undefined;

  currentPeriodItems: RaceCommercialItemPeriod[] = [];
  currency: string = "";
  priceConstantByPeriodChangeSubscription: Subscription | undefined;
  overlappingPeriods: { period1: RaceCommercialItemPeriod, period2: RaceCommercialItemPeriod }[] = []

  inputForm = new FormGroup({
    registrationFeeTypeId: new FormControl(),
    name: new FormControl(),
    nameEng: new FormControl(),
    nameIta: new FormControl(),
    nameSlo: new FormControl(),
    nameSr: new FormControl(),
    nameBs: new FormControl(),
    priceConstantByPeriod: new FormControl(),
    price: new FormControl(),
    vat: new FormControl(),
    vatExplanation: new FormControl(),
    vatExplanationEng: new FormControl(),
    vatExplanationIta: new FormControl(),
    vatExplanationSlo: new FormControl(),
    vatExplanationSr: new FormControl(),
    vatExplanationBs: new FormControl(),
    internalDescription: new FormControl(),
    internalDescriptionEng: new FormControl(),
    internalDescriptionSlo: new FormControl(),
    internalDescriptionIta: new FormControl(),
    internalDescriptionSr: new FormControl(),
    internalDescriptionBs: new FormControl(),
    periods: this.formBuilder.array([]),
  });

  constructor(public translocoService: TranslocoService, public formBuilder: FormBuilder, public activeModal: NgbActiveModal,
              private toastService: ToastService) {
    super();
  }

  ngOnInit(): void {
    this.subscribeToPriceValueChange(this.inputForm.controls.price);
    this.subscribeToVatChange();
    this.initForm(this.currentRaceCommercialItem);
    this.setVatExplanationControl();
    this._isRaceCommercialPeriodsValid(this.currentRaceCommercialItem.periods);
  }


  private subscribeToPriceValueChange(formControl: FormControl) {
    this.subscriptions.push(formControl.valueChanges.subscribe((_) => {
      this.setPriceDependantFields()
    }));
  }

  private subscribeToVatChange() {
    this.subscriptions.push(this.inputForm.controls.vat.valueChanges.subscribe(vat => {
      if (vat && this.vatExplanationControlWriteable()) {
        this.enableVatExplanation();
      } else {
        this.disableVatExplanation();
      }
    }))
  }


  private areAllPricesZero() {
    let allPricesZero = false;
    if (this.inputForm.controls.priceConstantByPeriod.value) {
      if (!this.inputForm.controls.price.value) {
        allPricesZero = true;
      }
    } else {
      for (let i = 0; i < this.periods.length; i++) {
        allPricesZero = true;
        if (this.periods.at(i).get("price")?.value) {
          allPricesZero = false;
          break;
        }
      }
    }
    return allPricesZero
  }

  private setPriceDependantFields() {
    if (this.areAllPricesZero()) {
      this.inputForm.controls.vat.removeValidators(required);
    } else {
      this.inputForm.controls.vat.setValidators(required);
    }
    this.inputForm.controls.vat.markAsUntouched();
    this.inputForm.controls.vat.updateValueAndValidity();
  }

  getCurrencyLabel(): string {
    const curr = this.currencies.find((currency) => currency.id === this.event?.currency);
    return curr?.name ?? "";
  }

  /**
   * BS - If the VAT explanation is entered, then in addition to the chosen language of the event, it must also be in Croatian.
   */
  getExtraLanguages(): number[] {
    let extraLanguages: number[] = this.event?.extraLanguages ?? [];

    if (!extraLanguages?.includes(Language.HR)) {
      extraLanguages?.push(Language.HR);
    }
    return extraLanguages;
  }

  private vatExplanationControlWriteable() {
    return this.organiserPaymentData?.paysPDV === true && this.event?.paymentSettings?.issuingInvoice === true;
  }

  private setVatExplanationControl() {
    if (this.vatExplanationControlWriteable() && !this.areAllPricesZero()) {
      this.enableVatExplanation();
    } else {
      this.disableVatExplanation();
    }
  }

  private disableVatExplanation() {
    this.inputForm.controls.vatExplanation.setValue(undefined);
    this.inputForm.controls.vatExplanationEng.setValue(undefined);
    this.inputForm.controls.vatExplanationIta.setValue(undefined);
    this.inputForm.controls.vatExplanationSlo.setValue(undefined);
    this.inputForm.controls.vatExplanationSr.setValue(undefined);
    this.inputForm.controls.vatExplanationBs.setValue(undefined);
    this.inputForm.controls.vatExplanation.disable();
    this.inputForm.controls.vatExplanationEng.disable();
    this.inputForm.controls.vatExplanationIta.disable();
    this.inputForm.controls.vatExplanationSlo.disable();
    this.inputForm.controls.vatExplanationSr.disable();
    this.inputForm.controls.vatExplanationBs.disable();
  }

  private enableVatExplanation() {
    this.inputForm.controls.vatExplanation.enable();
    this.inputForm.controls.vatExplanationEng.enable();
    this.inputForm.controls.vatExplanationIta.enable();
    this.inputForm.controls.vatExplanationSlo.enable();
    this.inputForm.controls.vatExplanationSr.enable();
    this.inputForm.controls.vatExplanationBs.enable();
  }


  private setValidatorsOnPriceBasedOnPriceConstantByPeriod(isConstant: boolean) {
    if (isConstant) {
      this.inputForm.controls.price.setValidators(required);
    } else {
      this.inputForm.controls.price.removeValidators(required);
      this.inputForm.controls.price.setValue(undefined);
    }
    this.inputForm.controls.price.updateValueAndValidity();
  }

  get periods(): FormArray {
    return this.inputForm.controls.periods as FormArray;
  }

  addPeriod() {
    let period = this.formBuilder.group({
      periodDates: ['', required], price: ['', required]
    });
    this.subscribeToPriceValueChange(period.controls.price);
    this.periods.push(period);
    let raceCommercialItemPeriod: RaceCommercialItemPeriod = new RaceCommercialItemPeriod();
    this.currentPeriodItems.push(raceCommercialItemPeriod);
    this.inputForm.updateValueAndValidity();
  }

  deletePeriod(index: number) {
    this.periods.removeAt(index);
    this.currentPeriodItems.splice(index, 1);
    if ((this.currentPeriodItems.length ?? 0) === 0) {
      this.addPeriod();
    }
    this.setPriceDependantFields()
  }

  onSave(): void {
    if (!this.inputForm.valid) {
      this.markAllAsTouched(this.inputForm);
      return;
    } else if (!this.race?.id) {
      this.toastService.showToast(TOAST_STATE.danger,
        this.translocoService.translate("organiser.race.details.save.error.no.raceId"));
      return;
    } else {
      let raceCommercialItem: RaceCommercialItem = this.getRaceCommercialItemFields();
      if (!this._isRaceCommercialItemValid(raceCommercialItem)) {
        return;
      }
      raceCommercialItem.raceId = this.race?.id;
      this.activeModal.close(raceCommercialItem)
    }
  }

  /**
   * Check that there isn't already a race commercial item with same data in list of race commercial items
   * @param raceCommercialItemArg
   */
  _isRaceCommercialItemValid(raceCommercialItemArg: RaceCommercialItem) {
    let isRaceCommercialItemValid: boolean = true;
    if (!this.raceCommercialItems || this.raceCommercialItems!.length === 0) {
      return isRaceCommercialItemValid;
    }
    if (raceCommercialItemArg.registrationFeeTypeId === RACE_COMMERCIAL_ITEM_DEFAULT) {
      let raceCommercialItem: RaceCommercialItem | undefined = this.raceCommercialItems?.find((raceCommercialItem: RaceCommercialItem) => raceCommercialItem.registrationFeeTypeId === RACE_COMMERCIAL_ITEM_DEFAULT);
      if (raceCommercialItem && raceCommercialItemArg.id !== raceCommercialItem.id) {
        isRaceCommercialItemValid = false;
        this.toastService.showToast(TOAST_STATE.danger,
          this.translocoService.translate("race.pricing.item.error.existingDefaultItem"));
      }
    }
    if (raceCommercialItemArg.priceConstantByPeriod === false) {
      if ((raceCommercialItemArg.periods === undefined || raceCommercialItemArg.periods?.length === 0)) {
        isRaceCommercialItemValid = false;
        this.toastService.showToast(TOAST_STATE.danger,
          this.translocoService.translate("race.pricing.item.error.periods.empty"));
      } else if (isRaceCommercialItemValid) {
        isRaceCommercialItemValid = this._isRaceCommercialPeriodsValid(raceCommercialItemArg.periods);
      }
    }
    return isRaceCommercialItemValid;
  }

  _isRaceCommercialPeriodsValid(periods?: RaceCommercialItemPeriod[]): boolean {
    this.overlappingPeriods = [];
    if (periods) {
      this.overlappingPeriods = RaceCommercialItemPeriod.checkForOverlaps((periods))
    }
    return this.overlappingPeriods.length === 0;
  }

  getRaceCommercialItemFields(): RaceCommercialItem {
    this.checkValidators(this.inputForm);
    let raceCommercialItem: RaceCommercialItem = {...this.currentRaceCommercialItem}
    raceCommercialItem.registrationFeeTypeId = this.inputForm.value.registrationFeeTypeId;
    raceCommercialItem.name = this.inputForm.value.name;
    raceCommercialItem.nameEng = this.inputForm.value.nameEng;
    raceCommercialItem.nameSlo = this.inputForm.value.nameSlo;
    raceCommercialItem.nameIta = this.inputForm.value.nameIta;
    raceCommercialItem.nameSr = this.inputForm.value.nameSr;
    raceCommercialItem.nameBs = this.inputForm.value.nameBs;
    raceCommercialItem.priceConstantByPeriod = this.inputForm.value.priceConstantByPeriod;
    raceCommercialItem.price = Number(this.inputForm.value.price);
    raceCommercialItem.internalDescription = this.inputForm.value.internalDescription;
    raceCommercialItem.internalDescriptionEng = this.inputForm.value.internalDescriptionEng;
    raceCommercialItem.internalDescriptionIta = this.inputForm.value.internalDescriptionIta;
    raceCommercialItem.internalDescriptionSlo = this.inputForm.value.internalDescriptionSlo;
    raceCommercialItem.internalDescriptionSr = this.inputForm.value.internalDescriptionSr;
    raceCommercialItem.internalDescriptionBs = this.inputForm.value.internalDescriptionBs;
    let raceCommercialItemPeriods: RaceCommercialItemPeriod[] = [];
    if (this.periods.length > 0) {
      for (let i: number = 0; i < this.periods.length; i++) {
        raceCommercialItemPeriods.push(new RaceCommercialItemPeriod(undefined, this.periods.at(i).get('periodDates')!.value.from,
          this.periods.at(i).get('periodDates')!.value.to, this.periods.at(i).get('price')!.value))
      }
    }
    raceCommercialItem.periods = !raceCommercialItem.priceConstantByPeriod ? raceCommercialItemPeriods : undefined;
    if (!this.areAllPricesZero()) {
      raceCommercialItem.vat = Number(this.inputForm.value.vat);
      raceCommercialItem.vatExplanation = this.inputForm.value.vatExplanation;
      raceCommercialItem.vatExplanationEng = this.inputForm.value.vatExplanationEng;
      raceCommercialItem.vatExplanationSlo = this.inputForm.value.vatExplanationSlo;
      raceCommercialItem.vatExplanationIta = this.inputForm.value.vatExplanationIta;
      raceCommercialItem.vatExplanationSr = this.inputForm.value.vatExplanationSr;
      raceCommercialItem.vatExplanationBs = this.inputForm.value.vatExplanationBs;
    } else {
      raceCommercialItem.vat = undefined;
      raceCommercialItem.vatExplanation = undefined;
      raceCommercialItem.vatExplanationEng = undefined;
      raceCommercialItem.vatExplanationSlo = undefined;
      raceCommercialItem.vatExplanationIta = undefined;
      raceCommercialItem.vatExplanationSr = undefined;
      raceCommercialItem.vatExplanationBs = undefined;
    }
    return raceCommercialItem;
  }

  initForm(raceCommercialItem: RaceCommercialItem) {
    this.inputForm.markAsUntouched();
    this.priceConstantByPeriodChangeSubscription?.unsubscribe();
    this.inputForm.reset();
    if (raceCommercialItem.priceConstantByPeriod === undefined) {
      raceCommercialItem.priceConstantByPeriod = true;
    }
    this.inputForm.patchValue(raceCommercialItem);
    this.inputForm.controls.price.setValue(raceCommercialItem.price);
    this.inputForm.controls.vat.setValue(raceCommercialItem.vat);
    this.inputForm.controls.registrationFeeTypeId.setValidators(required);
    this.inputForm.controls.periods = this.formBuilder.array([]);
    this.currentPeriodItems = raceCommercialItem.periods ? [...raceCommercialItem.periods] : [];

    if (this.currentPeriodItems.length > 0) {
      for (let i: number = 0; i < this.currentPeriodItems.length; i++) {
        let period = this.formBuilder.group({
          periodDates: ['', required],
          price: ['', required]
        });
        this.subscribeToPriceValueChange(period.controls.price);
        this.periods.push(period);
        this.periods.at(i).get('price')!.setValue(this.currentPeriodItems[i].price);
        this.periods.at(i).get('periodDates')!.setValue({
          from: this.currentPeriodItems[i].startDate,
          to: this.currentPeriodItems[i].endDate
        });
      }
    }
    this.setValidatorsOnPriceBasedOnPriceConstantByPeriod(raceCommercialItem.priceConstantByPeriod);
    this.priceConstantByPeriodChangeSubscription = this.inputForm.controls.priceConstantByPeriod.valueChanges.subscribe(value => {
      this.currentPeriodItems = [];
      this.inputForm.controls.periods = this.formBuilder.array([]);
      if (value === false) {
        this.addPeriod();
      }
      this.setValidatorsOnPriceBasedOnPriceConstantByPeriod(value);
    })
    this.inputForm.updateValueAndValidity();
  }

  getDate(date: Date | undefined) {
    return date ? new Date(date) : undefined
  }

  onDismissClick(): void {
    this.activeModal.dismiss();
  }

  override ngOnDestroy(): void {
    super.ngOnDestroy();
    this.priceConstantByPeriodChangeSubscription?.unsubscribe();
  }
}
